import { useCallback } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { Typeahead, type TypeaheadResult } from "@/components/Typeahead";
import {
  accountMatchLine,
  listAccounts,
  type AccountSummary,
} from "@/lib/brokerAccounts";

// BRK-S315 — finding an account as the broker types: `Typeahead`'s pause,
// cancellation and Try again, on the own-scoped `GET /api/broker/accounts`.
// Each match says which account it is, as two insureds often share a name.

export function AccountTypeahead({
  label,
  placeholder,
  selected = null,
  onSelect,
}: {
  /** The visible label and the combobox's name: distinct from the header's
   *  "Search all accounts" (BRK-S074). */
  label: string;
  placeholder?: string;
  selected?: AccountSummary | null;
  onSelect: (account: AccountSummary | null) => void;
}) {
  const { authedRequest } = useAuth();
  // Stable, as Typeahead requires.
  const search = useCallback(
    async (q: string): Promise<TypeaheadResult<AccountSummary>> => {
      const page = await listAccounts(authedRequest, { search: q, limit: 10 });
      return { items: page.items, emptyMessage: `No accounts match “${q}”.` };
    },
    [authedRequest],
  );
  return (
    <Typeahead
      label={label}
      placeholder={placeholder}
      search={search}
      selected={selected}
      onSelect={onSelect}
      keyOf={(a) => a.id}
      labelOf={(a) => a.legalName}
      renderOption={(a) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-brk-text">
            {a.legalName}
          </span>
          <span className="truncate text-sm text-brk-dim">
            {accountMatchLine(a)}
          </span>
        </span>
      )}
    />
  );
}
